import { thor } from "../../../typings/thor";
import { ImportSaleItemsService, Row as ItemRow } from "../../../sales/edit/add-item/import-sale-items.service";
import { OnlineItemGet } from "../../../typings/online";

enum OnlineCsvHeaders {
    OrderNbrPrimary = "orderNumberPrimary",
    OrderNbrSecondary = "orderNumberSecondary",
    Desc = "description",
    EstimMin = "estimateMin",
    EstimMax = "estimateMax",
    StartingPrice = "startingPrice"
}

type Row = ItemRow & {
    startingPrice: number;
};

export class ImportOnlineSaleItemsService extends ImportSaleItemsService {
    public static $inject: string[] = ["$q", "$rootScope", "$translate", "itemService", "utilsService"];

    constructor(
        $q: ng.IQService,
        $rootScope: ng.IRootScopeService,
        $translate: ng.translate.ITranslateService,
        itemService,
        utils
    ) {
        super($q, $rootScope, $translate, itemService, utils);

        this.csvHeaders = Object.values(OnlineCsvHeaders);
    }

    protected isCorrectRow(row: Row): boolean {
        return this.checkMatchedItems(row) && this.checkCorrectRow(row) && this.checkOnlineCorrectRow(row);
    }

    protected checkMatchedItems(row: Row): boolean {
        const findMatchedItems = this.findMatchedItems(row);
        const { estimateMin, startingPrice } = this.formatRowData(row);

        if (!findMatchedItems?.length) {
            return true;
        }

        return findMatchedItems.every(item => {
            if (this.isItemModified(item, row) && this.sale.resource.saleStarted) {
                this.summary.errors.push({
                    row,
                    message: this.$t("mise_a_jour_impossible_la_vente_du_lot_que_vous_souhaitez_modifier_est_deja_commencee")
                });
                return false;
            }

            const itemReservePrice = item.pricing?.reserve_price || 0;

            if (!itemReservePrice) {
                return true;
            }

            if (estimateMin && itemReservePrice > estimateMin) {
                this.summary.errors.push({
                    row,
                    message: this.$t("mise_a_jour_impossible_le_prix_de_reserve_est_superieur_a_l_estimation_basse")
                });
                return false;
            }

            if (itemReservePrice < startingPrice) {
                this.summary.errors.push({
                    row,
                    message: this.$t("mise_a_jour_impossible_le_prix_de_reserve_est_inferieur_a_la_mise_a_prix")
                });
                return false;
            }
            return true;
        });
    }

    protected checkOnlineCorrectRow(row: Row): boolean {
        const { estimateMin, estimateMax } = this.formatRowData(row);

        if (!estimateMin && estimateMax) {
            this.summary.errors.push({
                row,
                message: this.$t("mise_a_jour_impossible_l_estimation_basse_est_manquante")
            });
            return false;
        }

        if (estimateMin && !estimateMax) {
            this.summary.errors.push({
                row,
                message: this.$t("mise_a_jour_impossible_l_estimation_haute_est_manquante")
            });
            return false;
        }

        return true;
    }

    protected updateItem(item: OnlineItemGet, row: Row) {
        if (!this.isItemModified(item, row)) {
            this.summary.notModified++;
            return this.$q.resolve();
        }

        return this.itemService.update(item.id, this.formatData(row)).then(() => this.summary.updated++);
    }

    protected formatData(row: Row) {
        const data: thor.ItemPost = {
            description: row.description,
            meta: {
                order_number: {
                    primary: row.orderNumberPrimary
                }
            },
            pricing: null
        };

        if (row.orderNumberSecondary) {
            data.meta.order_number.secondary = row.orderNumberSecondary.toString();
        }

        if (row.estimateMin || row.estimateMax) {
            data.pricing = { estimates: {} };

            if (row.estimateMin) {
                data.pricing.estimates.min = row.estimateMin;
            }

            if (row.estimateMax) {
                data.pricing.estimates.max = row.estimateMax;
            }
        }

        if (row.startingPrice) {
            data.pricing = data.pricing ?? {};
            data.pricing.starting_price = row.startingPrice;
        }

        return data;
    }

    private formatItemData(item: OnlineItemGet) {
        return {
            description: this.normalizeStringNewLine(item.description),
            estimateMin: item.pricing?.estimates?.min || 0,
            estimateMax: item.pricing?.estimates?.max || 0,
            startingPrice: item.pricing?.starting_price || 0
        };
    }

    private formatRowData(row: Row) {
        return {
            description: this.normalizeStringNewLine(row.description),
            estimateMin: Number(row.estimateMin) || 0,
            estimateMax: Number(row.estimateMax) || 0,
            startingPrice: Number(row.startingPrice) || 0
        };
    }

    private isItemModified(item: OnlineItemGet, row: Row): boolean {
        return !angular.equals(this.formatItemData(item), this.formatRowData(row));
    }

    private normalizeStringNewLine(str = ""): string {
        return `${JSON.stringify(str).replace(/(\\r\\n|\\r|\\n)/gm, "\n")}`;
    }
}
